import { motion } from "framer-motion";
import { RefreshCcw, Tag, History, Plus } from "lucide-react";
import AdminGate from "@/components/AdminGate";
import useReviewData, { formatDate } from "@/components/review/useReviewData";
import PendingReviewSection from "@/components/review/PendingReviewSection";
import ActionHistorySection from "@/components/review/ActionHistorySection";
import StratagemCard from "@/components/review/StratagemCard";
import ReviewSidebar from "@/components/review/ReviewSidebar";
import AddStratagemModal from "@/components/review/AddStratagemModal";
import { fadeInUp, transition } from "@/utils/motion";

const ReviewContent = () => {
  const d = useReviewData();

  return (
    <div className="max-w-7xl mx-auto px-6 py-12 md:px-12" data-testid="review-page">
      {/* Header */}
      <motion.section
        {...fadeInUp}
        transition={transition}
        className="hud-card clip-corners p-6"
      >
        <div className="flex flex-wrap items-center justify-between gap-4">
          <div>
            <h1 className="font-headings text-4xl uppercase tracking-widest text-[#FFE800]" data-testid="review-title">
              Review Console
            </h1>
            <p className="text-sm text-gray-400" data-testid="review-subtitle">
              Manage stratagems synced from GitHub. Review new additions before release.
            </p>
          </div>
          <div className="flex flex-wrap gap-2">
            <button
              onClick={() => d.setShowAddModal(true)}
              className="flex items-center gap-2 border border-green-500/40 px-4 py-2 text-xs font-data text-green-400 hover:border-green-400"
              data-testid="review-add-button"
            >
              <Plus className="h-4 w-4" />
              Add Stratagem
            </button>
            <button
              onClick={d.handleRefresh}
              disabled={d.syncing}
              className="flex items-center gap-2 border border-[#FFE800]/40 px-4 py-2 text-xs font-data text-[#FFE800] hover:border-[#FFE800] disabled:opacity-50"
              data-testid="review-refresh-button"
            >
              <RefreshCcw className={`h-4 w-4 ${d.syncing ? "animate-spin" : ""}`} />
              Sync
            </button>
          </div>
        </div>

        {d.syncStatus && (
          <div className="mt-4 border border-white/10 p-3 text-xs text-gray-400" data-testid="sync-status">
            <span className="text-white">Source:</span> {d.syncStatus.source || "GitHub"} |{" "}
            <span className="text-white">Last sync:</span> {d.syncStatus.last_run ? formatDate(d.syncStatus.last_run) : "Never"} |{" "}
            <span className="text-white">Count:</span> {d.syncStatus.last_update_count || 0}
          </div>
        )}

        {/* Search */}
        <div className="mt-6">
          <input
            value={d.search}
            onChange={(e) => d.setSearch(e.target.value)}
            placeholder="Search stratagem name"
            className="w-full bg-black/50 border border-white/10 text-[#FFE800] font-data text-sm py-2 px-3 focus:border-[#FFE800] outline-none"
            data-testid="review-search-input"
          />
        </div>

        {/* Filters */}
        <div className="mt-4 flex flex-wrap items-center gap-3" data-testid="review-filter">
          <button
            onClick={() => d.setFilter("all")}
            className={`border px-3 py-2 text-xs font-headings uppercase tracking-[0.2em] ${
              d.filter === "all" ? "border-[#FFE800] text-[#FFE800]" : "border-white/10 text-gray-400 hover:text-[#FFE800]"
            }`}
            data-testid="review-filter-all"
          >
            All ({d.activeItems.length})
          </button>
          {d.newCount > 0 && (
            <button
              onClick={() => d.setFilter("new")}
              className={`border px-3 py-2 text-xs font-headings uppercase tracking-[0.2em] ${
                d.filter === "new" ? "border-green-400 text-green-400" : "border-green-500/40 text-green-400 hover:border-green-400"
              }`}
              data-testid="review-filter-new"
            >
              New ({d.newCount})
            </button>
          )}
          {d.allCategories.slice(0, 6).map((cat) => (
            <button
              key={cat}
              onClick={() => d.setFilter(cat)}
              className={`border px-3 py-2 text-xs font-headings uppercase tracking-[0.2em] ${
                d.filter === cat ? "border-[#FFE800] text-[#FFE800]" : "border-white/10 text-gray-400 hover:text-[#FFE800]"
              }`}
              data-testid={`review-filter-${cat.toLowerCase().replace(/\s+/g, "-")}`}
            >
              {cat}
            </button>
          ))}
          {d.allCategories.length > 6 && (
            <button
              onClick={() => d.setShowCategoryManager(!d.showCategoryManager)}
              className="border border-white/10 px-3 py-2 text-xs font-data text-gray-400"
              data-testid="review-more-categories"
            >
              +{d.allCategories.length - 6} more
            </button>
          )}
        </div>

        {/* Quick actions */}
        <div className="mt-4 flex flex-wrap items-center gap-3">
          <button
            onClick={d.handleAutoAssignCategories}
            className="flex items-center gap-2 border border-white/20 px-3 py-2 text-xs font-data text-white"
            data-testid="review-auto-assign"
          >
            <Tag className="h-4 w-4" />
            Auto-assign categories
          </button>
          <button
            onClick={() => d.setShowHistory(!d.showHistory)}
            className={`flex items-center gap-2 border px-3 py-2 text-xs font-data ${
              d.showHistory ? "border-[#FFE800] text-[#FFE800]" : "border-white/20 text-white"
            }`}
            data-testid="review-toggle-history"
          >
            <History className="h-4 w-4" />
            Action History ({d.actionHistory.length})
          </button>
        </div>

        {d.loading && <p className="mt-4 text-sm text-gray-400" data-testid="review-loading">Loading stratagem data...</p>}
        {d.error && <p className="mt-4 text-sm text-red-400" data-testid="review-error">{d.error}</p>}
      </motion.section>

      <PendingReviewSection
        items={d.pendingItems}
        approveSettings={d.approveSettings}
        updateApproveSettings={d.updateApproveSettings}
        approveStratagem={d.approveStratagem}
        onBulkApproved={d.fetchAllData}
      />

      {d.showHistory && <ActionHistorySection history={d.actionHistory} onUndo={d.undoStratagem} />}

      {/* Main content grid */}
      <div className="mt-8 grid gap-6 lg:grid-cols-[1.2fr_0.8fr]" data-testid="review-content">
        <div className="space-y-4" data-testid="review-list">
          {d.filtered.map((item) => (
            <StratagemCard
              key={item.id}
              item={item}
              isEditing={d.editingId === item.id}
              editingValues={d.editingValues}
              setEditingValues={d.setEditingValues}
              categoryInput={d.categoryInput}
              setCategoryInput={d.setCategoryInput}
              beginEdit={d.beginEdit}
              cancelEdit={d.cancelEdit}
              saveOverride={d.saveOverride}
              deleteStratagem={d.deleteStratagem}
              undoStratagem={d.undoStratagem}
              addDirection={d.addDirection}
              removeLastDirection={d.removeLastDirection}
              clearDirections={d.clearDirections}
              addCategory={d.addCategory}
              removeCategory={d.removeCategory}
            />
          ))}
        </div>

        <ReviewSidebar
          activeItems={d.activeItems}
          pendingItems={d.pendingItems}
          deletedItems={d.deletedItems}
          allCategories={d.allCategories}
          newCount={d.newCount}
          filter={d.filter}
          setFilter={d.setFilter}
          showCategoryManager={d.showCategoryManager}
          undoStratagem={d.undoStratagem}
        />
      </div>

      {d.showAddModal && (
        <AddStratagemModal
          newStratagem={d.newStratagem}
          setNewStratagem={d.setNewStratagem}
          newCategoryInput={d.newCategoryInput}
          setNewCategoryInput={d.setNewCategoryInput}
          addNewDirection={d.addNewDirection}
          removeNewLastDirection={d.removeNewLastDirection}
          clearNewDirections={d.clearNewDirections}
          addNewCategory={d.addNewCategory}
          removeNewCategory={d.removeNewCategory}
          createStratagem={d.createStratagem}
          resetNewStratagem={d.resetNewStratagem}
        />
      )}
    </div>
  );
};

const Review = ({ embedded }) => {
  if (embedded) return <ReviewContent />;
  return (
    <AdminGate>
      <ReviewContent />
    </AdminGate>
  );
};

export default Review;
